/**
 * One tap on a follow control (ISSUE-1370, ISSUE-1372). "Follow" sends the
 * request straight away. "Requested" and "Following" both undo, and both
 * confirm first through the shared `confirmDestructive`, naming the fan.
 *
 * The answer the server gives is held as an override until the invalidated
 * reads catch up, so the button never flickers back to its old label while
 * the refetch is in the air. Failures go to the shared error modal.
 */
import {
  useFanFollowCancel,
  useFanFollowRequest,
} from "@sportsmet/profile-sdk/private";
import { confirmDestructive } from "@sportsmet/ui";
import { useState } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import {
  followChangedAnnouncement,
  UNFOLLOW_CONFIRM_BODY,
  UNFOLLOW_CONFIRM_LABEL,
  unfollowConfirmTitle,
  WITHDRAW_CONFIRM_BODY,
  WITHDRAW_CONFIRM_LABEL,
  withdrawConfirmTitle,
} from "./followCopy";
import { isFollowState, type typeFollowState } from "./followState";

export type typeFollowToggle = {
  /** The state to render: the server's last answer to a tap, else the read's. */
  state: typeFollowState;
  isPending: boolean;
  /** Set after a successful tap, for an aria-live line. */
  announcement: string | null;
  toggle: () => Promise<void>;
};

export function useFollowToggle({
  accountId,
  displayName,
  state: readState,
}: {
  accountId: string;
  displayName: string;
  state: typeFollowState;
}): typeFollowToggle {
  const request = useFanFollowRequest();
  const cancel = useFanFollowCancel();
  const [override, setOverride] = useState<{
    from: typeFollowState;
    to: typeFollowState;
  } | null>(null);
  const [announcement, setAnnouncement] = useState<string | null>(null);

  // The override only stands while the read still says what it said when the
  // tap happened. Once the read moves, the read is the truth again.
  const state =
    override !== null && override.from === readState ? override.to : readState;

  async function toggle(): Promise<void> {
    if (state !== "none") {
      const isConfirmed = await confirmDestructive(
        state === "following"
          ? {
              title: unfollowConfirmTitle(displayName),
              body: UNFOLLOW_CONFIRM_BODY,
              confirmLabel: UNFOLLOW_CONFIRM_LABEL,
            }
          : {
              title: withdrawConfirmTitle(displayName),
              body: WITHDRAW_CONFIRM_BODY,
              confirmLabel: WITHDRAW_CONFIRM_LABEL,
            },
      );
      if (!isConfirmed) return;
    }
    try {
      const result =
        state === "none"
          ? await request.mutateAsync({ accountId })
          : await cancel.mutateAsync({ accountId });
      if (showApiError(result)) return;
      const next = result.data?.followState;
      if (isFollowState(next)) {
        setOverride({ from: readState, to: next });
        setAnnouncement(followChangedAnnouncement(next, displayName, state));
      }
    } catch {
      showOfflineError();
    }
  }

  return {
    state,
    isPending: request.isPending || cancel.isPending,
    announcement,
    toggle,
  };
}
